import React from 'react';
import { TableRow, TableWrapper, Td, Th, THead, Table } from '@increasecard/typed-components';
import { Chargeback, Currency as CurrencyType } from '@card/types';
import Paginator from '../../../common/components/Paginator';
import { useTranslation } from 'react-i18next';
import { useLocale, Currency } from '@increasecard/increase-core';
import { formatDate, LoadingOverlay, UpdateChargebackOptions } from '@card/commonlib';
import CardBrandIcon from '../../../common/components/CardBrandIcon';
import Status from './Status';
import Responsible from './Responsible';
import DetailsModal from './DetailsModal';

interface ChargebacksListProps {
  currentPage: number;
  currency: string;
  fetchingData: boolean;
  onPageSelected: (page: number) => void;
  onDataChanged: (params: UpdateChargebackOptions) => void;
  chargebacks: Chargeback[];
  totalPages: number;
}

const List = ({
  chargebacks,
  currency,
  fetchingData,
  totalPages,
  currentPage,
  onDataChanged,
  onPageSelected
}: ChargebacksListProps) => {
  const { t } = useTranslation();
  const locale = useLocale();
  const hasData = chargebacks && chargebacks.length > 0;
  if (locale === null) return null;
  return (
    <>
      <LoadingOverlay active={fetchingData} style={{ top: '1px', left: '-1px' }}>
        <TableWrapper>
          <Table hasData={hasData} zebra>
            <THead>
              <tr>
                <Th>{t('chargebacks:List:Headers:date')}</Th>
                <Th>{t('chargebacks:List:Headers:establishmentNumber')}</Th>
                <Th>{t('chargebacks:List:Headers:settlement')}</Th>
                <Th>{t('chargebacks:List:Headers:shopName')}</Th>
                <Th>{t('chargebacks:List:Headers:type')}</Th>
                <Th>{t('chargebacks:List:Headers:description')}</Th>
                <Th rightAligned>{t('chargebacks:List:Headers:expiration')}</Th>
                <Th rightAligned>{t('chargebacks:List:Headers:amount')}</Th>
                <Th>{t('chargebacks:List:Headers:status')}</Th>
                <Th>{t('chargebacks:List:Headers:responsible')}</Th>
              </tr>
            </THead>
            <tbody>
              {chargebacks.map((chargeback, index) => {
                const onChargebackDataChanged = (field: string, value: string) => {
                  const params: UpdateChargebackOptions = {
                    chargebackId: chargeback.id,
                    options: {
                      [field]: value
                    }
                  };
                  return onDataChanged(params);
                };
                return (
                  <TableRow key={index} style={{ height: 60 }}>
                    <Td>{formatDate(chargeback.presentation_date, locale)}</Td>
                    <Td>{chargeback.stablishment_number}</Td>
                    <Td>
                      <CardBrandIcon cardBrand={chargeback.type} />
                    </Td>
                    <Td>{chargeback.shop_name}</Td>
                    <Td>
                      {t(`chargebacks:List:fields:type:${chargeback.category.replace('::', '')}`)}
                    </Td>
                    <Td>{chargeback.description || '-'}</Td>
                    <Td rightAligned>{formatDate(chargeback.date, locale)}</Td>
                    <Td rightAligned>
                      <Currency
                        currency={currency as CurrencyType}
                        value={Number(chargeback.total_amount.amount) / 100}
                      />
                    </Td>
                    <Td>
                      <Status
                        onStatusSelected={onChargebackDataChanged}
                        value={chargeback.status}
                      />
                    </Td>
                    <Td>
                      <Responsible
                        onResponsibleChanged={onChargebackDataChanged}
                        value={chargeback.responsible}
                      />
                    </Td>
                    <Td rightAligned>
                      <DetailsModal chargeback={chargeback} />
                    </Td>
                  </TableRow>
                );
              })}
            </tbody>
          </Table>
          {hasData && (
            <Paginator
              align="right"
              onSelectPage={onPageSelected}
              pages={totalPages}
              selected={currentPage}
            />
          )}
        </TableWrapper>
      </LoadingOverlay>
    </>
  );
};

export default List;
